"use client";

import Image from "next/image";
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";

const IMAGE_SRC = "/image/new-version/fabrication/metal-welding/automated-welding-with-js-precision.webp";

const GlobeIcon = ({ className }: { className?: string }) => (
	<svg
		className={`h-14 w-14 sm:h-16 sm:w-16 md:h-[72px] md:w-[72px] lg:h-20 lg:w-20 ${className ?? ""}`}
		viewBox="0 0 48 48"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="24" cy="24" r="20" />
		<ellipse cx="24" cy="24" rx="20" ry="8" />
		<ellipse cx="24" cy="24" rx="8" ry="20" />
	</svg>
);

const FEATURES = [
	{ titleKey: "automatedWeldingFeature1Title", descKey: "automatedWeldingFeature1Desc" },
	{ titleKey: "automatedWeldingFeature2Title", descKey: "automatedWeldingFeature2Desc" },
	{ titleKey: "automatedWeldingFeature3Title", descKey: "automatedWeldingFeature3Desc" },
] as const;

export default function AutomatedWeldingWithJSPrecision() {
	const t = useTranslations("new-welding");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="relative mb-8 flex min-h-[72px] items-center sm:mb-10 sm:min-h-[80px] md:mb-12 md:min-h-[88px]">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						<span className="block">{t("automatedWeldingTitleLine1")}</span>
						<span className="block">{t("automatedWeldingTitleLine2")}</span>
					</h2>
					<div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
						<GlobeIcon className="text-themePrimary400" />
					</div>
				</div>

				<div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-8 xl:gap-10">
					<div className="flex w-full flex-col lg:min-w-0 lg:flex-1">
						<div className="relative aspect-[4/3] w-full overflow-hidden shadow-lg lg:aspect-auto lg:h-full lg:min-h-[480px]">
							<Image
								src={IMAGE_SRC}
								alt={t("automatedWeldingHeroImageAlt")}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>
					</div>

					<div className="flex w-full min-w-0 flex-1 flex-col justify-center lg:min-w-0">
						<ul className="mb-8 space-y-5">
							{FEATURES.map((feature, index) => (
								<li key={index} className="text-sm leading-relaxed sm:text-base">
									<p className="mb-2 text-base font-bold text-black sm:text-lg">{t(feature.titleKey)}</p>
									<p className="text-sm leading-relaxed text-black md:text-base">{t(feature.descKey)}</p>
								</li>
							))}
						</ul>

						<Link
							href="/upload"
							className="inline-flex w-fit items-center justify-center rounded bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#83b751] hover:text-white md:px-8 md:py-3.5 md:text-base"
						>
							{t("automatedWeldingGetQuote")}
						</Link>
					</div>
				</div>
			</div>
			<div className="container mt-10 sm:mt-12 md:mt-14 lg:mt-16">
				<div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-20">
					<div className="lg:max-w-[40%] lg:shrink-0">
						<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
							{t("metalWeldingDefinitionTitle")}
						</h2>
					</div>
					<div className="min-w-0 flex-1">
						<p className="text-sm leading-relaxed text-black md:text-base">
							{t("metalWeldingDefinitionDesc")}
						</p>
					</div>
				</div>
			</div>
		</section>
	);
}
